import type {SWRConfiguration} from 'swr';
import useSWR from 'swr';

import {baseLogger} from '../helpers';
import type {ApiError} from '../types';

import type {BaseClient, CacheKey, ExtractEndpointRequestType, ExtractEndpointResponseType} from './types';
import {isValidCacheKey} from './types';

/** @description React hook that accepts client generated by openapi-generator typescript-fetch */
const useSWRApi = <
  TClient extends BaseClient,
  TKey extends CacheKey<object, TClient>,
  TRequest extends ExtractEndpointRequestType<TClient, TKey[`endpoint`]>,
  TResponse extends ExtractEndpointResponseType<TClient, TKey[`endpoint`]>,
  // TODO: infer error type from client
  TError extends any | ApiError = any,
>(
  client: TClient,
  key: TKey,
  request: TRequest = {} as TRequest,
  config: SWRConfiguration<TResponse, any> = {},
) => {
  const fetcher = (cacheKey: TKey) => {
    if (!isValidCacheKey(cacheKey)) {
      console.warn(`Invalid cache key`, cacheKey);
      return null;
    }

    baseLogger.debug(`Fetching data for cache key`, cacheKey);

    const {endpoint, query: params} = cacheKey;
    if (!(endpoint in client) || typeof client[endpoint] !== `function`) {
      return Promise.reject(new Error(`Invalid endpoint: ${endpoint}`));
    }
    // We need to bind `this` as the apiClient, and pass the
    // request as the only argument
    return (client[endpoint] as Function).apply(client, [params ?? {}]);
  };

  // TODO: add error type
  return useSWR<TResponse, TError>({...key, query: request}, fetcher, config);
};

export default useSWRApi;
